import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ChessBoard from './ChessBoard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initializeBoard, isValidMove, makeMove } from '../utils/chessLogic';
import { getTutorialLessons } from '../utils/tutorialLessons';

const { FiHome, FiChevronRight, FiCheck, FiSkipForward, FiRefreshCw, FiLightbulb } = FiIcons;

const InteractiveTutorial = () => {
  const navigate = useNavigate();
  const [currentLesson, setCurrentLesson] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [board, setBoard] = useState(initializeBoard());
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showHint, setShowHint] = useState(false);

  const lessons = getTutorialLessons();
  const lesson = lessons[currentLesson];
  const step = lesson?.steps[currentStep];

  useEffect(() => {
    if (step) {
      setBoard(step.board || initializeBoard());
      setSelectedSquare(null);
      setValidMoves([]);
      setShowHint(false);
    }
  }, [currentLesson, currentStep, step]);

  const handleSquareClick = (row, col) => {
    if (!step || step.type !== 'move') return;

    const piece = board[row][col];

    if (selectedSquare) {
      const [selectedRow, selectedCol] = selectedSquare;
      
      if (selectedRow === row && selectedCol === col) {
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }

      // Check if this is the expected move
      if (step.expectedMove) {
        const expectedFrom = step.expectedMove.from;
        const expectedTo = step.expectedMove.to;
        
        if (
          selectedRow === expectedFrom[0] &&
          selectedCol === expectedFrom[1] &&
          row === expectedTo[0] &&
          col === expectedTo[1]
        ) {
          // Correct move!
          const moveResult = makeMove(board, selectedRow, selectedCol, row, col, 'white');
          if (moveResult.success) {
            setBoard(moveResult.newBoard);
            setCompletedSteps(prev => new Set([...prev, currentStep]));
            
            // Auto-advance after a short delay
            setTimeout(() => {
              nextStep();
            }, 1500);
          }
        } else {
          // Wrong move
          setSelectedSquare(null);
          setValidMoves([]);
          setShowHint(true);
        }
      } else {
        // Generic move validation
        if (validMoves.some(move => move.row === row && move.col === col)) {
          const moveResult = makeMove(board, selectedRow, selectedCol, row, col, 'white');
          if (moveResult.success) {
            setBoard(moveResult.newBoard);
            setCompletedSteps(prev => new Set([...prev, currentStep]));
            
            setTimeout(() => {
              nextStep();
            }, 1500);
          }
        }
      }
      
      setSelectedSquare(null);
      setValidMoves([]);
    } else if (piece && piece.color === 'white') {
      // Check if this is the piece we should select
      if (step.expectedMove && step.expectedMove.from) {
        const expectedFrom = step.expectedMove.from;
        if (row === expectedFrom[0] && col === expectedFrom[1]) {
          setSelectedSquare([row, col]);
          setValidMoves(isValidMove(board, row, col, 'white'));
        } else {
          setShowHint(true);
        }
      } else {
        setSelectedSquare([row, col]);
        setValidMoves(isValidMove(board, row, col, 'white'));
      }
    }
  };

  const nextStep = () => {
    if (currentStep < lesson.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else if (currentLesson < lessons.length - 1) {
      setCurrentLesson(prev => prev + 1);
      setCurrentStep(0);
      setCompletedSteps(new Set());
    } else {
      // Tutorial completed
      navigate('/');
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else if (currentLesson > 0) {
      setCurrentLesson(prev => prev - 1);
      const prevLesson = lessons[currentLesson - 1];
      setCurrentStep(prevLesson.steps.length - 1);
    }
  };

  const skipStep = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    nextStep();
  };

  const resetStep = () => {
    if (step) {
      setBoard(step.board || initializeBoard());
      setSelectedSquare(null);
      setValidMoves([]);
      setShowHint(false);
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentStep);
        return newSet;
      });
    }
  };

  if (!lesson || !step) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading tutorial...</div>
      </div>
    );
  }

  const isStepCompleted = completedSteps.has(currentStep);

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-4xl font-bold text-white mb-2">
            {lesson.title}
          </h1>
          <p className="text-gray-300 text-lg">{lesson.description}</p>
          
          {/* Progress */}
          <div className="mt-4 max-w-md mx-auto">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>Lesson {currentLesson + 1} of {lessons.length}</span>
              <span>Step {currentStep + 1} of {lesson.steps.length}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${((currentLesson * 100 + ((currentStep + 1) / lesson.steps.length) * 100) / lessons.length)}%` 
                }}
              />
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Instructions Panel */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 mb-6"
            >
              <div className="flex items-center gap-2 mb-4">
                {isStepCompleted ? (
                  <SafeIcon icon={FiCheck} className="text-2xl text-green-400" />
                ) : (
                  <SafeIcon icon={FiLightbulb} className="text-2xl text-yellow-400" />
                )}
                <h3 className="text-xl font-bold text-white">
                  {step.title}
                </h3>
              </div>
              
              <p className="text-gray-300 mb-4">
                {step.instruction}
              </p>

              {step.type === 'move' && step.expectedMove && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3 mb-4">
                  <p className="text-blue-300 text-sm">
                    <strong>Task:</strong> Move the piece from{' '}
                    {String.fromCharCode(97 + step.expectedMove.from[1])}{8 - step.expectedMove.from[0]}{' '}
                    to{' '}
                    {String.fromCharCode(97 + step.expectedMove.to[1])}{8 - step.expectedMove.to[0]}
                  </p>
                </div>
              )}

              {showHint && step.hint && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4"
                >
                  <p className="text-yellow-300 text-sm">
                    <strong>Hint:</strong> {step.hint}
                  </p>
                </motion.div>
              )}

              {isStepCompleted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 mb-4"
                >
                  <p className="text-green-300 text-sm font-semibold">
                    ✓ Step completed! Well done!
                  </p>
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={resetStep}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    <SafeIcon icon={FiRefreshCw} />
                    Reset
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={skipStep}
                    className="flex items-center gap-2 px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    <SafeIcon icon={FiSkipForward} />
                    Skip
                  </motion.button>
                </div>

                {isStepCompleted && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={nextStep}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    {currentStep === lesson.steps.length - 1 && currentLesson === lessons.length - 1 ? (
                      'Complete Tutorial'
                    ) : currentStep === lesson.steps.length - 1 ? (
                      'Next Lesson'
                    ) : (
                      'Next Step'
                    )}
                    <SafeIcon icon={FiChevronRight} />
                  </motion.button>
                )}
              </div>
            </motion.div>

            {/* Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <h4 className="text-white font-semibold mb-3">Tutorial Navigation</h4>
              <div className="space-y-2">
                {lessons.map((tutorialLesson, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded-lg text-sm ${
                      index === currentLesson
                        ? 'bg-blue-500/20 text-blue-300'
                        : index < currentLesson
                        ? 'bg-green-500/20 text-green-300'
                        : 'bg-white/5 text-gray-400'
                    }`}
                  >
                    {index + 1}. {tutorialLesson.title}
                  </div>
                ))}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
              >
                <SafeIcon icon={FiHome} />
                Exit Tutorial
              </motion.button>
            </motion.div>
          </div>

          {/* Chess Board */}
          <div className="lg:col-span-2 order-1 lg:order-2 flex justify-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <ChessBoard
                board={board}
                selectedSquare={selectedSquare}
                validMoves={validMoves}
                onSquareClick={handleSquareClick}
                currentPlayer="white"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTutorial;